import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Button } from './ui/button';
import { formatInZone } from '../lib/timezone';
import { X, History, RotateCcw, User, Clock, FileText, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductVersionHistory({ productId, getAuthHeader, onClose, onRestore }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [loadingSnapshot, setLoadingSnapshot] = useState(false);
    const [restoring, setRestoring] = useState(false);

    useEffect(() => {
        loadVersions();
    }, [productId]);

    const loadVersions = async () => {
        try {
            const res = await axios.get(`${API}/products/${productId}/versions`, { headers: getAuthHeader() });
            setVersions(res.data);
        } catch {
            toast.error('Failed to load version history');
        } finally {
            setLoading(false);
        }
    };

    const viewVersion = async (version) => {
        setSelectedVersion(version);
        setLoadingSnapshot(true);
        try {
            const res = await axios.get(`${API}/products/${productId}/versions/${version.version}`, { headers: getAuthHeader() });
            setSnapshot(res.data.snapshot);
        } catch {
            toast.error('Failed to load version');
        } finally {
            setLoadingSnapshot(false);
        }
    };

    const handleRestore = async () => {
        if (!selectedVersion) return;
        if (!window.confirm(`Restore to version ${selectedVersion.version}? The current content will be saved as a new version.`)) return;
        setRestoring(true);
        try {
            await axios.post(
                `${API}/products/${productId}/versions/${selectedVersion.version}/restore`,
                {},
                { headers: getAuthHeader() },
            );
            toast.success(`Restored to version ${selectedVersion.version}`);
            onRestore?.();
        } catch {
            toast.error('Failed to restore version');
        } finally {
            setRestoring(false);
        }
    };

    const formatDate = (dateStr) => {
        // #541: absolute timestamps route through the shared tz-aware
        // formatter (active viewer zone: profile → browser → UTC).
        try {
            return formatInZone(dateStr, { formatStr: 'MMM d, yyyy h:mm a' }) || dateStr;
        } catch {
            return dateStr;
        }
    };

    // Portaled to <body> (#496): ProductDetail's root is `.animate-fade-in`,
    // whose transform makes it the containing block for `position: fixed`
    // descendants while the fade runs, so an inline overlay would be laid out
    // against the page instead of the viewport.
    return createPortal(
        <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />

            {/* Panel */}
            <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-slide-in-right">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                    <div className="flex items-center gap-2">
                        <History className="w-5 h-5 text-stone-600" />
                        <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Version History
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="w-6 h-6 border-3 border-stone-700 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : versions.length === 0 ? (
                        <div className="text-center py-12">
                            <History className="w-10 h-10 mx-auto text-stone-200 mb-3" />
                            <p className="text-sm text-stone-500">No version history yet</p>
                            <p className="text-xs text-stone-400 mt-1">Versions are created automatically when you save changes</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-stone-100">
                            {/* Version snapshot view */}
                            {selectedVersion && (
                                <div className="p-5 bg-stone-50 border-b border-stone-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => { setSelectedVersion(null); setSnapshot(null); }}
                                                className="text-xs text-stone-500 hover:text-stone-700"
                                            >
                                                ← Back to list
                                            </button>
                                        </div>
                                        <Button
                                            onClick={handleRestore}
                                            disabled={restoring}
                                            size="sm"
                                            className="bg-stone-900 hover:bg-stone-800 text-xs"
                                        >
                                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                            {restoring ? 'Restoring...' : 'Restore This Version'}
                                        </Button>
                                    </div>

                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="px-2 py-0.5 bg-stone-200 rounded-full text-xs font-semibold text-stone-700">
                                            v{selectedVersion.version}
                                        </span>
                                        <span className="text-xs text-stone-500">
                                            by {selectedVersion.changed_by_name || 'Unknown'} • {formatDate(selectedVersion.created_at)}
                                        </span>
                                    </div>

                                    {loadingSnapshot ? (
                                        <div className="flex items-center justify-center h-24">
                                            <div className="w-5 h-5 border-2 border-stone-600 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    ) : snapshot ? (
                                        <div className="space-y-3">
                                            <div className="bg-white rounded-lg p-3 border border-stone-200">
                                                <h4 className="text-sm font-semibold text-stone-700">{snapshot.name}</h4>
                                                <p className="text-xs text-stone-400 font-mono mt-0.5">{snapshot.sku}</p>
                                                {snapshot.description && (
                                                    <p className="text-xs text-stone-500 mt-2 line-clamp-3">{snapshot.description}</p>
                                                )}
                                            </div>
                                            {snapshot.content_html && (
                                                <div className="bg-white rounded-lg p-3 border border-stone-200">
                                                    <p className="text-[10px] uppercase text-stone-400 font-semibold tracking-wider mb-1.5">Content Preview</p>
                                                    <div
                                                        className="prose prose-sm max-h-40 overflow-y-auto text-xs text-stone-600"
                                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snapshot.content_html || '') }}
                                                    />
                                                </div>
                                            )}
                                            {snapshot.sections?.length > 0 && (
                                                <div className="bg-white rounded-lg p-3 border border-stone-200">
                                                    <p className="text-[10px] uppercase text-stone-400 font-semibold tracking-wider mb-1.5">
                                                        {snapshot.sections.length} section{snapshot.sections.length !== 1 ? 's' : ''}
                                                    </p>
                                                    {snapshot.sections.map((s, i) => (
                                                        <div key={i} className="flex items-center gap-2 py-1 text-xs text-stone-500">
                                                            <FileText className="w-3 h-3 text-stone-300" />
                                                            <span>{s.title}</span>
                                                            <span className="text-[10px] text-stone-300">({s.type})</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {/* Version list */}
                            {!selectedVersion && versions.map((v) => (
                                <button
                                    key={v.version_id}
                                    onClick={() => viewVersion(v)}
                                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-stone-50 transition-colors text-left"
                                >
                                    <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-xs font-bold text-stone-600">v{v.version}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-stone-700">
                                                {v.change_summary || `Version ${v.version}`}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-400">
                                            <span className="flex items-center gap-1">
                                                <User className="w-3 h-3" /> {v.changed_by_name || 'Unknown'}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> {formatDate(v.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
